import { CardTitle, Heading } from '@lmc/design-system'
import { cssVars, tokens } from '@lmc/design-system/tokens'
import type { ThemeConfig } from 'antd'
import type { ReactNode } from 'react'
import styled, { css } from 'styled-components'

const { color, spacing, text } = cssVars

export const DATE_FORMAT = 'DD-MM-YYYY'
export const TIME_FORMAT = 'HH:mm'

/**
 * antd's controls in Body M and 40px tall, as designed: the app's theme leaves
 * selects, pickers, ticks and radios at antd's 14px and every field at 32px.
 * antd derives a 24px line height from the 16px size, and its tick and radio
 * size from the height, so that is pinned at the designed 16px. antd needs
 * colour values, not variables.
 */
export const stepAntdTheme: ThemeConfig = {
  token: {
    colorText: tokens.color.text.primary,
    colorTextPlaceholder: tokens.color.text.input.placeholder,
    controlHeight: tokens.size[500],
    controlInteractiveSize: tokens.size[200],
    fontSize: tokens.text.body.md.size
  }
}

/** A step's or a case card's title: Heading M, which `CardTitle` has no size for. */
export const CaseCardTitle = styled(CardTitle)`
  font-size: ${text.heading.md.size};
  line-height: ${text.heading.md.lineHeight};
`

/**
 * A field's error in Body M, as designed, over the app-wide 12px rule in
 * antDesign.less; that rule is `!important`, so this has to be too.
 */
export const stepFieldErrors = css`
  && .ant-form-item-explain-error,
  && .ant-form-item-explain-warning {
    font-size: ${text.body.md.size} !important;
    line-height: ${text.body.md.lineHeight};
  }
`

/** The design system's collapse point, shared by the form shell and its cards. */
export const STEP_COLLAPSE = '(max-width: 768px)'

/** 32px between groups in the design: twice the 16px space token. */
export const stepGroupGap = `calc(${spacing[200]} * 2)`

type StepSectionProps = {
  /** The group's heading, e.g. "Incident time & details"; most have none. */
  title?: ReactNode
  children: ReactNode
}

/**
 * A group of fields in a step: 24px between fields, and 12px under the heading
 * when it has one. Groups are divided with `StepSeparator`.
 */
export const StepSection = ({ title, children }: StepSectionProps) =>
  title ? (
    <Titled>
      <Heading size="xs" weight="medium">
        {title}
      </Heading>
      <Fields>{children}</Fields>
    </Titled>
  ) : (
    <Fields>{children}</Fields>
  )

export const StepSeparator = styled.hr`
  border: 0;
  border-top: 1px solid ${color.border.subtle};
  margin: 0;
`

/** Two fields side by side, e.g. a date and a time, wrapping below 256px each. */
export const StepFieldPair = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${spacing[300]} ${spacing[200]};

  > * {
    flex: 1 0 256px;
  }
`

/** A column of ticks or radios, 12px apart as designed. */
export const stepChoiceColumn = css`
  display: flex;
  flex-direction: column;
  gap: ${spacing[150]};
`

const Titled = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing[150]};
`

/** antd's own item margin is turned off so it can't add to the 24px gap. */
const Fields = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing[300]};

  .ant-form-item {
    margin-bottom: 0;
  }

  .ant-picker {
    width: 100%;
  }
`
